import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

interface ImageViewerProps {
  images: { url: string; label: string }[];
  initialIndex: number;
  onClose: () => void;
}

export function ImageViewer({ images, initialIndex, onClose }: ImageViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  let touchStartX = 0;

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goPrev();
    else if (e.key === 'ArrowRight') goNext();
    else if (e.key === 'Escape') onClose();
  }, [goPrev, goNext, onClose]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX = e.changedTouches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (dx > 50) goPrev();
    else if (dx < -50) goNext();
  };

  const current = images[currentIndex];
  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      autoFocus
    >
      {/* Close button */}
      <button
        className="absolute top-4 right-4 text-white/80 hover:text-white z-10"
        onClick={onClose}
      >
        <X className="h-8 w-8" />
      </button>

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          className="absolute left-2 md:left-6 text-white/60 hover:text-white z-10 p-2"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
        >
          <ChevronLeft className="h-10 w-10" />
        </button>
      )}

      {/* Image */}
      <div
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={current.url}
          alt={current.label}
          className="max-w-full max-h-[80vh] object-contain rounded"
          draggable={false}
        />
        <div className="mt-3 text-white/80 text-sm text-center">
          {current.label}
          {images.length > 1 && (
            <span className="ml-2 text-white/50">({currentIndex + 1} / {images.length})</span>
          )}
        </div>
      </div>

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          className="absolute right-2 md:right-6 text-white/60 hover:text-white z-10 p-2"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
        >
          <ChevronRight className="h-10 w-10" />
        </button>
      )}
    </div>
  );
}
